/**
 * All Versions Page
 *
 * Displays all versions across all tables with server-side pagination,
 * sorting, and filtering.
 *
 * Features:
 * - Server-side data grid with TanStack Query
 * - Export to Excel
 * - Delete versions with protection for "current" and "previous" tags
 * - View version progress
 */

import { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import {
  Files,
  Table as TableIcon,
  Download,
  Trash2,
  Eye,
  Plus,
  FileJson,
  FileSpreadsheet,
  FileText,
  FileArchive,
} from 'lucide-react';
import type { VersionExportFormat } from '@loader/shared-types';
import { Button } from '@loader/ui';
import { Badge } from '@loader/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@loader/ui';

import {
  DataTable,
  useDataGrid,
  createNameColumn,
  createTextColumn,
  createNumberColumn,
  createDateColumn,
} from '@/components/data-grid';
import {
  SHABadge,
  FoldingCube,
  EmptyView,
  ConfirmWithNameDialog,
  ErrorBoundary,
} from '@/components/common';
import { formatDateTime, formatDateShort, formatBytes } from '@/lib/formatters';
import { useRpc } from '@/lib/rpc';
import { toast } from '@/lib/toast';

// ============================================================================
// Types
// ============================================================================

/**
 * Version row data structure
 * Note: Uses snake_case to match API response
 */
interface VersionRow {
  id: string;
  tableName?: string;
  tableDescription?: string | null;
  file_name: string;
  file_size: number;
  status: string;
  records_processed?: number;
  created_at: string;
  system_tag?: 'current' | 'previous' | null;
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * All Versions Page Component
 *
 * Displays all versions across all tables with server-side data grid.
 */
function AllVersionsPageContent() {
  // ============================================================================
  // Hooks & State
  // ============================================================================

  const navigate = useNavigate();
  const rpc = useRpc();
  const queryClient = useQueryClient();

  // Local UI state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState<VersionRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ============================================================================
  // Data Fetching
  // ============================================================================

  // Server-side data grid with TanStack Query
  const grid = useDataGrid<VersionRow>({
    endpoint: 'versions',
    initialPageSize: 20,
    initialSortBy: 'created_at',
    initialSortDir: 'desc',
    queryKeyPrefix: 'versions',
  });

  // ============================================================================
  // Event Handlers
  // ============================================================================

  /**
   * Handle export for a version in the specified format
   */
  const handleExport = useCallback(
    async (version: VersionRow, format: VersionExportFormat) => {
      try {
        // @ts-expect-error - RPC types may not be fully generated yet
        const res = await rpc.api.private.versions[':id'].export.$get({
          param: { id: version.id },
          query: { format },
        });

        if (!res.ok) {
          throw new Error('Failed to export version');
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${version.file_name}_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        // Download started automatically via browser - no toast needed
      } catch (error) {
        toast.version.exportFailed(error instanceof Error ? error.message : undefined);
      }
    },
    [rpc]
  );

  /**
   * Open delete confirmation dialog
   */
  const handleDelete = useCallback((version: VersionRow) => {
    setVersionToDelete(version);
    setDeleteDialogOpen(true);
  }, []);

  /**
   * Confirm and execute version deletion with grid refresh
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!versionToDelete) return;

    try {
      setIsDeleting(true);

      // @ts-expect-error - RPC types may not be fully generated yet
      const res = await rpc.api.private.versions[':id'].$delete({
        param: { id: versionToDelete.id },
      });

      if (!res.ok) {
        const errorData = await res.json();
        const error = new Error(errorData.error || 'Failed to delete version');
        // Preserve status and additional data
        (error as Error & { status: number; dependentLinkedTables?: string[] }).status = res.status;
        if ('dependentLinkedTables' in errorData) {
          (error as Error & { status: number; dependentLinkedTables?: string[] }).dependentLinkedTables = errorData.dependentLinkedTables;
        }
        throw error;
      }

      // No toast - UI update shows version is gone

      // Invalidate all version-related queries:
      // - versions-all: the all versions grid on this page
      // - dataGrid queries with /versions endpoint
      // - tables queries: includes table-specific version lists used by ViewTable pickers
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            // All versions grid
            (Array.isArray(key) && key[0] === 'versions-all') ||
            // DataGrid queries for versions
            (Array.isArray(key) &&
              key[0] === 'dataGrid' &&
              typeof key[1] === 'string' &&
              key[1].includes('/api/private/versions')) ||
            // Table-specific version queries (used by ViewTable version pickers)
            // Query key pattern: ['tables', 'detail', tableId, 'versions']
            (Array.isArray(key) && key[0] === 'tables')
          );
        },
      });

      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setVersionToDelete(null);
    } catch (error) {
      // Check for 409 dependency conflict
      if (
        error instanceof Error &&
        'status' in error &&
        (error as Error & { status: number; dependentLinkedTables?: string[] })
          .status === 409
      ) {
        const dependentLinkedTables = (
          error as Error & { status: number; dependentLinkedTables?: string[] }
        ).dependentLinkedTables;
        if (dependentLinkedTables && dependentLinkedTables.length > 0) {
          toast.version.deleteBlocked(dependentLinkedTables);
        } else {
          toast.version.deleteFailed(error.message);
        }
      } else {
        toast.version.deleteFailed(error instanceof Error ? error.message : undefined);
      }
    } finally {
      setIsDeleting(false);
    }
  }, [versionToDelete, rpc, queryClient]);

  /**
   * Handle navigation to version progress page
   */
  const handleLinkedTableProgress = useCallback((version: VersionRow) => {
    window.location.href = `/versions/progress?versionId=${version.id}`;
  }, []);

  /**
   * Handle navigation to create new version
   */
  const handleCreateVersion = useCallback(() => {
    void navigate({
      to: '/versions/new',
      search: { tableId: undefined },
    });
  }, [navigate]);

  // ============================================================================
  // Column Definitions
  // ============================================================================

  /**
   * Memoized column definitions for the versions table
   */
  const columns = useMemo<ColumnDef<VersionRow>[]>(
    () => [
      createNameColumn<VersionRow>('tableName', 'Table', {
        descriptionKey: 'tableDescription',
        icon: TableIcon,
        minWidth: 250,
        maxWidth: 400,
        cellRenderer: (value, original) => {
          const version = original as VersionRow;
          const tableName = String(value) || 'Unknown Table';
          const description = version.tableDescription || '';

          return (
            <div className="flex items-center gap-3">
              <div className="bg-blue-500/10 rounded-lg p-2 shrink-0">
                <TableIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{tableName}</div>
                {description && (
                  <div className="text-sm text-muted-foreground truncate">
                    {description}
                  </div>
                )}
              </div>
            </div>
          );
        },
        filterFn: 'includesString',
      }),
      createNameColumn<VersionRow>('file_name', 'File', {
        icon: Files,
        minWidth: 250,
        maxWidth: 400,
        cellRenderer: (value, original) => {
          const version = original as VersionRow;
          const fileName = String(value);
          const fileSize = (version.file_size / 1024 / 1024).toFixed(1);

          return (
            <div className="flex items-center gap-3">
              <div className="bg-green-500/10 rounded-lg p-2 shrink-0">
                <Files className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{fileName}</div>
                <div className="text-sm text-muted-foreground truncate">
                  {fileSize} MB
                </div>
              </div>
            </div>
          );
        },
        filterFn: 'includesString',
      }),
      // SHA (first 8 chars of version UUID)
      createTextColumn<VersionRow>('id', 'SHA', {
        minWidth: 100,
        maxWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterFn: 'includesString',
        cellRenderer: (_value, original) => {
          const version = original as VersionRow;
          return <SHABadge id={version.id} />;
        },
      }),
      createTextColumn<VersionRow>('status', 'Status', {
        cellRenderer: (value) => {
          const status = String(value);
          const variant =
            status === 'completed'
              ? 'success'
              : status === 'processing'
                ? 'info'
                : status === 'pending'
                  ? 'warning'
                  : 'destructive';
          return <Badge variant={variant}>{status}</Badge>;
        },
      }),
      createNumberColumn<VersionRow>('records_processed', 'Records', {
        cellRenderer: (value) => {
          const records = value as number;
          return records ? records.toLocaleString() : '-';
        },
        filterFn: 'basic',
      }),
      createDateColumn<VersionRow>('created_at', 'Versioned At', {
        cellRenderer: (value) => {
          return (
            <div className="text-sm">
              {formatDateTime(value as string)}
            </div>
          );
        },
      }),
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const version = row.original as VersionRow;
          const isProtected = version.system_tag === 'current' || version.system_tag === 'previous';

          return (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLinkedTableProgress(version);
                }}
                title="View Progress"
                aria-label="View Progress"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => e.stopPropagation()}
                    title="Export"
                    aria-label="Export"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuItem onClick={() => handleExport(version, 'json')}>
                    <FileJson className="mr-2 h-4 w-4" />
                    JSON
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport(version, 'csv')}>
                    <FileText className="mr-2 h-4 w-4" />
                    CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport(version, 'xlsx')}>
                    <FileSpreadsheet className="mr-2 h-4 w-4" />
                    Excel (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport(version, 'parquet')}>
                    <FileArchive className="mr-2 h-4 w-4" />
                    Parquet
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isProtected) {
                    toast.warning(
                      'Cannot delete version',
                      'This version is currently tagged as Current or Previous. Promote another version first.'
                    );
                    return;
                  }
                  handleDelete(version);
                }}
                title={
                  isProtected
                    ? 'Cannot delete protected version'
                    : 'Delete version'
                }
                aria-label="Delete version"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ],
    [handleExport, handleDelete, handleLinkedTableProgress]
  );

  // ============================================================================
  // Early Returns (Loading, Error)
  // ============================================================================

  if (grid.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <FoldingCube text="Loading versions..." />
      </div>
    );
  }

  if (grid.error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-destructive">Error: {grid.error.message}</div>
      </div>
    );
  }

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="absolute inset-4 top-0 flex flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {/* Page Header: align with tables/linked tables layouts */}
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Versions</h1>
            <p className="text-muted-foreground">
              Review every file version across your tables
              {typeof grid.pagination.total === 'number'
                ? ` (${grid.pagination.total} total)`
                : ''}
            </p>
          </div>
          <Button size="sm" onClick={handleCreateVersion} className="shrink-0">
            <Plus className="mr-2 h-4 w-4" />
            Create Version
          </Button>
        </header>

        {/* Main Content - Versions Table */}
        <div className="min-h-0 flex-1">
          <DataTable
            data={grid.data as VersionRow[]}
            columns={columns}
            // Server pagination
            pageIndex={grid.pagination.pageIndex}
            pageSize={grid.pagination.pageSize}
            totalItems={grid.pagination.total}
            pageCount={grid.pagination.pageCount}
            onPageChange={grid.actions.setPage}
            onPageSizeChange={grid.actions.setPageSize}
            // Sorting
            sortBy={grid.sorting.sort_by}
            sortDir={grid.sorting.sort_dir}
            onSortChange={grid.actions.setSort}
            // Filtering
            filters={grid.filters}
            onFilterChange={grid.actions.setFilter}
            onFilterRemove={grid.actions.removeFilter}
            onFiltersClear={grid.actions.clearFilters}
            // UI
            enableFiltering={true}
            enablePagination={true}
            showFooter={true}
            hideVerticalLines={true}
            emptyStateComponent={
              <EmptyView
                title="No versions found"
                description="Versions track changes to your tables over time. Create your first version to get started."
                icon={Files}
              >
                <Button asChild>
                  <Link to="/versions/new" search={{ tableId: undefined }}>Create your first version</Link>
                </Button>
              </EmptyView>
            }
          />
        </div>
      </div>

      {/* Delete Version Confirmation Dialog */}
      {versionToDelete && (
        <ConfirmWithNameDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Delete Version"
          itemName={versionToDelete.file_name}
          itemType="Version"
          metadata={[
            ...(versionToDelete.tableName
              ? [{ label: 'Table', value: versionToDelete.tableName }]
              : []),
            { label: 'Created', value: formatDateShort(versionToDelete.created_at) },
            ...(versionToDelete.records_processed !== undefined
              ? [{ label: 'Records', value: versionToDelete.records_processed.toLocaleString() }]
              : []),
            { label: 'Size', value: formatBytes(versionToDelete.file_size) },
            ...(versionToDelete.system_tag
              ? [{ label: 'System Tag', value: versionToDelete.system_tag }]
              : []),
          ]}
          isLoading={isDeleting}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
}

export function AllVersionsPage() {
  return (
    <ErrorBoundary>
      <AllVersionsPageContent />
    </ErrorBoundary>
  );
}
